import React, { useEffect, useMemo, useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Edit3, Loader2, Plus, Trash2 } from "lucide-react";
import { PageContainer, PageHeader, Pill } from "@/components/layout/PageShell";
import { DataToolbar, DataTable } from "@/components/layout/DataTable";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { useCurrency } from "@/hooks/useCurrency";
import api from "@/lib/api";
import { toast } from "sonner";
import { resolveMediaUrl } from "@/lib/mediaUrl";
import { apiError, listPriceLabel, priceSortValue } from "./menuItemFormUtils";

const PER_PAGE = 25;
const ALL = "all";

function useDebounced(value, delay = 350) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

export default function MenuItemMaster() {
  const { user } = useAuth();
  const { format } = useCurrency();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const branchId = user?.default_branch?.id;
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [categoryId, setCategoryId] = useState(ALL);
  const [subcategoryId, setSubcategoryId] = useState(ALL);
  const debouncedSearch = useDebounced(search);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, categoryId, subcategoryId]);

  const { data: mainMenus = [] } = useQuery({
    queryKey: ["main-menus", branchId],
    queryFn: async () => {
      const { data } = await api.get("/main-menus", { params: { branch_id: branchId } });
      return data.main_menus ?? [];
    },
    enabled: Boolean(branchId),
  });

  const { data: subMenus = [] } = useQuery({
    queryKey: ["sub-menus", branchId],
    queryFn: async () => {
      const { data } = await api.get("/sub-menus", { params: { branch_id: branchId } });
      return data.sub_menus ?? [];
    },
    enabled: Boolean(branchId),
  });

  const subcategories = useMemo(() => {
    if (categoryId === ALL) return subMenus;
    return subMenus.filter((s) => String(s.main_menu_id) === categoryId);
  }, [subMenus, categoryId]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["menu-items", branchId, page, debouncedSearch, categoryId, subcategoryId],
    queryFn: async () => {
      const { data: res } = await api.get("/menu-items", {
        params: {
          branch_id: branchId,
          page,
          per_page: PER_PAGE,
          search: debouncedSearch || undefined,
          main_menu_id: categoryId !== ALL ? categoryId : undefined,
          sub_menu_id: subcategoryId !== ALL ? subcategoryId : undefined,
        },
      });
      return res;
    },
    enabled: Boolean(branchId),
    placeholderData: keepPreviousData,
  });

  const items = data?.menu_items ?? [];
  const meta = data?.meta ?? {};
  const total = meta.total ?? items.length;

  const deleteMutation = useMutation({
    mutationFn: async (itemId) =>
      api.delete(`/menu-items/${itemId}`, { params: { branch_id: branchId } }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["menu-items", branchId] });
      toast.success(res.data.message || "Menu item deleted.");
    },
    onError: (err) => toast.error(apiError(err, "Failed to delete menu item")),
  });

  function onCategoryChange(value) {
    setCategoryId(value);
    setSubcategoryId(ALL);
  }

  return (
    <PageContainer>
      <PageHeader
        title="Menu Item Master"
        subtitle={`${total} items — ${user?.default_branch?.name || "branch"}`}
        actions={
          <Button
            size="sm"
            className="h-9 bg-[#16A34A] hover:bg-emerald-700"
            onClick={() => navigate("/masters/menu-item/new")}
            data-testid="menu-item-add"
          >
            <Plus size={14} className="mr-1.5" />
            Add new
          </Button>
        }
      />
      <DataToolbar
        searchValue={search}
        onSearch={setSearch}
        testIdPrefix="menu-item"
        extra={
          <>
            <Select value={categoryId} onValueChange={onCategoryChange}>
              <SelectTrigger className="h-9 w-[180px]" data-testid="menu-item-category">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All categories</SelectItem>
                {mainMenus.map((mm) => (
                  <SelectItem key={mm.id} value={String(mm.id)}>{mm.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={subcategoryId} onValueChange={setSubcategoryId}>
              <SelectTrigger className="h-9 w-[200px]" data-testid="menu-item-subcategory">
                <SelectValue placeholder="Subcategory" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All subcategories</SelectItem>
                {subcategories.map((s) => (
                  <SelectItem key={s.id} value={String(s.id)}>
                    {categoryId === ALL && s.main ? `${s.main} › ${s.name}` : s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isFetching && !isLoading ? (
              <Loader2 size={16} className="animate-spin text-slate-400" />
            ) : null}
          </>
        }
      />
      {isLoading ? (
        <div className="h-32 grid place-items-center text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : (
        <DataTable
          testIdPrefix="menu-item"
          emptyText="No menu items yet"
          onRowClick={(row) => navigate(`/masters/menu-item/${row.id}/edit`)}
          pagination={{
            page: meta.current_page || page,
            lastPage: meta.last_page || 1,
            total,
            from: meta.from || 0,
            to: meta.to || 0,
            onPageChange: setPage,
          }}
          columns={[
            {
              key: "img",
              label: "Image",
              sortable: false,
              width: "72px",
              render: (v) => {
                const src = resolveMediaUrl(v);
                return src ? (
                  <img src={src} alt="" className="h-10 w-10 rounded-md object-cover border border-[#E5E7EB] bg-slate-50" />
                ) : (
                  <div className="h-10 w-10 rounded-md bg-slate-100 border border-[#E5E7EB] grid place-items-center text-[9px] font-bold text-slate-400">
                    —
                  </div>
                );
              },
            },
            { key: "code", label: "Code", mono: true, bold: true, sortable: true },
            { key: "name", label: "Name", bold: true, sortable: true },
            {
              key: "name_ar",
              label: "Arabic",
              sortable: true,
              render: (v) =>
                v ? (
                  <span dir="rtl" lang="ar" className="block text-right">
                    {v}
                  </span>
                ) : (
                  "—"
                ),
            },
            { key: "main_menu", label: "Category", sortable: true, render: (v) => v || "—" },
            { key: "sub_menu", label: "Subcategory", sortable: true, render: (v) => v || "—" },
            {
              key: "kitchen_station",
              label: "KOT",
              sortable: true,
              render: (v, row) => v?.code || row.kitchen_station?.code || "—",
            },
            { key: "unit", label: "Unit", sortable: true },
            {
              key: "price",
              label: "Price",
              align: "right",
              sortable: true,
              sortValue: priceSortValue,
              render: (_v, row) => listPriceLabel(row, format),
            },
            {
              key: "vat_percent",
              label: "VAT",
              align: "right",
              sortable: true,
              sortValue: (row) => Number(row.vat_percent ?? row.vat ?? 0),
              render: (_v, row) => `${Number(row.vat_percent ?? row.vat ?? 0)}%`,
            },
            {
              key: "show_in_pos",
              label: "POS",
              sortable: true,
              render: (v) => <Pill tone={v ? "green" : "default"}>{v ? "Yes" : "No"}</Pill>,
            },
            {
              key: "kot",
              label: "KOT",
              sortable: true,
              render: (v) => <Pill tone={v ? "green" : "default"}>{v ? "Yes" : "No"}</Pill>,
            },
            {
              key: "status",
              label: "Status",
              sortable: true,
              render: (v) => (
                <Pill tone={v === "Active" ? "green" : v === "OutOfStock" ? "red" : "default"}>
                  {v === "OutOfStock" ? "Out of stock" : v}
                </Pill>
              ),
            },
          ]}
          rows={items}
          rowActions={(row) => [
            { title: "Edit", icon: <Edit3 size={14} />, onClick: () => navigate(`/masters/menu-item/${row.id}/edit`) },
            {
              title: "Delete",
              icon: <Trash2 size={14} />,
              danger: true,
              onClick: () => {
                if (window.confirm(`Delete "${row.name}"? This cannot be undone.`)) {
                  deleteMutation.mutate(row.id);
                }
              },
            },
          ]}
        />
      )}
    </PageContainer>
  );
}
